import { resolve as dnsResolve } from 'node:dns/promises'
import type { Schedule, ScheduleFunction, ExecutionResult } from './schedules.types.js'

// ============================================================================
// SSRF Protection
// ============================================================================

const METADATA_HOSTNAMES = ['metadata.google.internal']

/**
 * Check if an IP address belongs to a private/reserved range.
 */
export function isPrivateIP(ip: string): boolean {
  // IPv6 special
  if (ip === '::1' || ip === '::' || ip.startsWith('fe80:') || ip.startsWith('fd') || ip.startsWith('fc')) {
    return true
  }

  // IPv4
  const parts = ip.split('.').map(Number)
  if (parts.length !== 4 || parts.some(p => isNaN(p))) return false

  const [a, b] = parts as [number, number, number, number]

  return (
    a === 127 ||                          // 127.0.0.0/8 loopback
    a === 10 ||                           // 10.0.0.0/8
    (a === 172 && b >= 16 && b <= 31) ||  // 172.16.0.0/12
    (a === 192 && b === 168) ||           // 192.168.0.0/16
    (a === 169 && b === 254) ||           // 169.254.0.0/16 link-local
    a === 0 ||                            // 0.0.0.0/8
    a === 255                             // broadcast
  )
}

/**
 * Validate an internal API path (no traversal, no query strings).
 */
export function assertSafeInternalPath(path: string): void {
  if (!path.startsWith('/')) {
    throw new Error('Internal path must start with /')
  }

  if (path.includes('..')) {
    throw new Error('Internal path contains path traversal')
  }

  if (path.includes('//')) {
    throw new Error('Internal path contains path traversal')
  }

  if (/[?#;|]/.test(path)) {
    throw new Error('Internal path contains unsafe characters')
  }
}

/**
 * Validate an external URL is safe (no SSRF).
 * Checks protocol, hostname, IP ranges, metadata endpoints, and DNS rebinding.
 */
export async function assertSafeExternalUrl(url: string): Promise<void> {
  const parsed = new URL(url) // throws on malformed

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw new Error(`Unsafe protocol: ${parsed.protocol}`)
  }

  const hostname = parsed.hostname

  if (hostname === 'localhost') {
    throw new Error('URL targets localhost')
  }

  // Check metadata hostnames
  if (METADATA_HOSTNAMES.includes(hostname)) {
    throw new Error('URL targets cloud metadata endpoint')
  }

  // Metadata IP check before generic private IP check
  if (hostname === '169.254.169.254') {
    throw new Error('URL targets cloud metadata endpoint')
  }

  // Direct IP check
  if (isPrivateIP(hostname)) {
    throw new Error(`URL targets private IP: ${hostname}`)
  }

  // DNS rebinding protection — resolve and check all IPs
  try {
    const ips = await dnsResolve(hostname) as string[]
    for (const ip of ips) {
      if (ip === '169.254.169.254') {
        throw new Error('URL resolves to cloud metadata endpoint')
      }
      if (isPrivateIP(ip)) {
        throw new Error(`URL resolves to private IP: ${ip}`)
      }
    }
  } catch (err) {
    // DNS resolution failed — let fetch fail naturally (ENOTFOUND)
    if ((err as Error).message.includes('private IP') || (err as Error).message.includes('metadata')) {
      throw err
    }
  }
}

// ============================================================================
// Function Registry
// ============================================================================

/**
 * Function registry for scheduled tasks
 */
const functionRegistry = new Map<string, ScheduleFunction>()

export function registerFunction(key: string, fn: ScheduleFunction): void {
  functionRegistry.set(key, fn)
}

export function unregisterFunction(key: string): void {
  functionRegistry.delete(key)
}

export function listRegisteredFunctions(): string[] {
  return Array.from(functionRegistry.keys())
}

export function hasFunctionRegistered(key: string): boolean {
  return functionRegistry.has(key)
}

export function clearFunctionRegistry(): void {
  functionRegistry.clear()
}

/**
 * Executes a schedule's registered function
 */
export async function executeTarget(schedule: Schedule): Promise<ExecutionResult> {
  const startTime = Date.now()

  try {
    const key = schedule.function_key
    const args = schedule.function_args ?? undefined
    const fn = functionRegistry.get(key)
    if (!fn) {
      throw new Error(`Function '${key}' not found in registry`)
    }
    const data = await fn(args)
    return { success: true, data, duration_ms: Date.now() - startTime }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
      duration_ms: Date.now() - startTime
    }
  }
}
